/**
 * Byte ranges of a file, from wherever the file is: a presigned URL
 * (httpRangeSource) or bytes in hand (bytesRangeSource). The zip and PDF
 * readers ask for what they need through one of these.
 */
export interface RangeSource {
  size: number
  /** Bytes [start, end). */
  read: (start: number, end: number) => Promise<Uint8Array>
}

export const bytesRangeSource = (bytes: Uint8Array): RangeSource => ({
  size: bytes.byteLength,
  read: async (start, end) => bytes.subarray(start, end),
})

interface HttpRangeSourceOptions {
  url: string
  size: number
  signal?: AbortSignal
  /**
   * A new presigned URL, for when the one in hand has expired. Asked for once,
   * by whichever read is refused first; the rest wait on the same answer. A
   * refusal of the new URL is an answer.
   */
  refreshUrl?: () => Promise<string>
  /**
   * Fail rather than take the whole file from a server that ignores the range:
   * for a reader with the whole file coming another way, where a second copy
   * would only double the download.
   */
  rangesOnly?: boolean
}

export class HttpRangeError extends Error {
  override name = 'HttpRangeError'
  constructor(readonly status: number) {
    super(`Range read failed (${status})`)
  }
}

/**
 * Byte ranges of a remote file. Each read is one GET with a closed range
 * (`bytes=a-b`), the form browsers send without a CORS preflight, so it works
 * against a bucket that allows no request headers at all.
 *
 * A server that ignores the range answers 200 with the whole file; the first
 * such body is kept and every read served from it, so a preview still works
 * there — it just costs what it did before. Reads that raced it let their own
 * copies go.
 */
export const httpRangeSource = ({
  url,
  size,
  signal,
  refreshUrl,
  rangesOnly = false,
}: HttpRangeSourceOptions): RangeSource & { wholeFile: boolean } => {
  let currentUrl = url
  let refreshing: Promise<string> | null = null
  let whole: Promise<Uint8Array> | null = null

  /**
   * One GET of its own, with a way to let it go. Aborted, not cancelled: a
   * cancelled body is read to its end by Chrome anyway, to keep the connection.
   */
  const request = (requestUrl: string, start: number, end: number) => {
    const controller = new AbortController()
    const abort = () => controller.abort()
    signal?.addEventListener('abort', abort, { once: true })
    if (signal?.aborted) abort()
    const response = fetch(requestUrl, {
      headers: { Range: `bytes=${start}-${end - 1}` },
      // Chrome keeps 206s as sparse cache entries and, when range reads of
      // one URL overlap, restarts some mid-body to settle the entry: the same
      // bytes, fetched twice. A presigned URL is re-signed on every fetch, so
      // the cache had little to give back anyway.
      cache: 'no-store',
      signal: controller.signal,
    })
    const settle = () => signal?.removeEventListener('abort', abort)
    return { response, letGo: abort, settle }
  }

  const fetchRange = async (
    start: number,
    end: number,
    retried = false,
  ): Promise<Uint8Array> => {
    const requestedUrl = currentUrl
    const { response, letGo, settle } = request(requestedUrl, start, end)
    try {
      const res = await response
      if (
        (res.status === 401 || res.status === 403) &&
        refreshUrl &&
        !retried
      ) {
        letGo()
        // Refused on the URL a refresh has already replaced: retry on the new one.
        if (requestedUrl === url) {
          refreshing ??= refreshUrl().then((fresh) => (currentUrl = fresh))
          await refreshing
        }
        return await fetchRange(start, end, true)
      }
      if (res.status === 206) return new Uint8Array(await res.arrayBuffer())
      if (res.status === 200) {
        if (rangesOnly || whole) {
          letGo()
          if (rangesOnly) throw new HttpRangeError(res.status)
        } else {
          source.wholeFile = true
          whole = res.arrayBuffer().then((buffer) => new Uint8Array(buffer))
        }
        return (await whole!).subarray(start, end)
      }
      letGo()
      throw new HttpRangeError(res.status)
    } finally {
      settle()
    }
  }

  const source = {
    size,
    wholeFile: false,
    read: (start: number, end: number) =>
      whole
        ? whole.then((bytes) => bytes.subarray(start, end))
        : fetchRange(start, Math.min(end, size)),
  }
  return source
}

/**
 * The first `maxBytes` of a remote text file, cut back to the last line break
 * so no line is shown half-read. `complete` says the file ended first.
 */
export const fetchTextHead = async ({
  url,
  maxBytes,
  signal,
  refreshUrl,
}: {
  url: string
  maxBytes: number
  signal?: AbortSignal
  refreshUrl?: () => Promise<string>
}): Promise<{ text: string; complete: boolean }> => {
  let res = await fetch(url, {
    headers: { Range: `bytes=0-${maxBytes - 1}` },
    signal,
  })
  if ((res.status === 401 || res.status === 403) && refreshUrl) {
    res = await fetch(await refreshUrl(), {
      headers: { Range: `bytes=0-${maxBytes - 1}` },
      signal,
    })
  }
  if (res.status === 416) return { text: '', complete: true }
  if (!res.ok || !res.body) throw new HttpRangeError(res.status)

  // A 200 carries the whole file; read only as far as a 206 would have.
  const reader = res.body.getReader()
  const chunks: Uint8Array[] = []
  let received = 0
  let ended = false
  while (received < maxBytes) {
    const { value, done } = await reader.read()
    if (done) {
      ended = true
      break
    }
    chunks.push(value)
    received += value.byteLength
  }
  if (!ended) void reader.cancel().catch(() => undefined)

  const total =
    res.status === 206
      ? Number(
          /\/(\d+)$/.exec(res.headers.get('Content-Range') ?? '')?.[1] ?? NaN,
        )
      : NaN
  const bytes = new Uint8Array(Math.min(received, maxBytes))
  let offset = 0
  for (const chunk of chunks) {
    const take = chunk.subarray(0, bytes.byteLength - offset)
    bytes.set(take, offset)
    offset += take.byteLength
    if (offset >= bytes.byteLength) break
  }
  // Content-Range is not an exposed header on the buckets, so a 206 that
  // filled the window is taken as a file that goes on.
  const complete =
    res.status === 206
      ? Number.isFinite(total)
        ? total <= maxBytes
        : bytes.byteLength < maxBytes
      : ended && received <= maxBytes

  const text = new TextDecoder().decode(bytes)
  if (complete) return { text, complete }
  const lastBreak = text.lastIndexOf('\n')
  return { text: lastBreak > 0 ? text.slice(0, lastBreak) : text, complete }
}
